import { trackStep } from "./analytics.js";
import { getMissingRequiredQuestions, hasAvailableResults, scoreResults } from "./scoring.js";
import { createElement, cssEscape, safeUrl } from "./utils.js";

export function renderQuiz(app, quiz, answers, currentIndex, shouldScroll) {
  app.innerHTML = "";
  currentIndex = Math.max(0, Math.min(currentIndex, quiz.questions.length - 1));
  const currentQuestion = quiz.questions[currentIndex];
  const isLastQuestion = currentIndex === quiz.questions.length - 1;

  trackStep(quiz, currentQuestion, currentIndex + 1, quiz.questions.length);

  const header = createElement("div", "bhpf__header");
  header.appendChild(createElement("h2", "bhpf__title", quiz.title));

  if (quiz.intro) {
    header.appendChild(createElement("p", "bhpf__intro", quiz.intro));
  }

  const form = createElement("form", "bhpf__form");
  form.noValidate = true;

  const stage = createElement("div", "bhpf__stage");
  stage.appendChild(renderProgress(currentIndex, quiz.questions.length));
  stage.appendChild(renderQuestion(currentQuestion, currentIndex, answers));
  form.appendChild(stage);

  const actions = createElement("div", "bhpf__actions");

  if (currentIndex > 0) {
    const back = createElement("button", "bhpf__button bhpf__button--secondary", "Back");
    back.type = "button";
    back.addEventListener("click", () => {
      renderQuiz(app, quiz, answers, currentIndex - 1, true);
    });
    actions.appendChild(back);
  }

  const submit = createElement("button", "bhpf__button", isLastQuestion ? quiz.button_label || "Show my recommendation" : "Next");
  submit.type = "submit";
  actions.appendChild(submit);
  form.appendChild(actions);

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    clearErrors(form);

    const missing = getMissingRequiredQuestions({ questions: [currentQuestion] }, answers);
    if (missing.length) {
      showMissingErrors(form, missing);
      return;
    }

    if (!hasAvailableResults(quiz, answers)) {
      showNoResultsMessage(form, quiz);
      return;
    }

    if (!isLastQuestion) {
      renderQuiz(app, quiz, answers, currentIndex + 1, true);
      return;
    }

    renderResultsPage(app, quiz, answers);
  });

  app.appendChild(header);
  app.appendChild(form);

  if (shouldScroll) {
    scrollActiveQuestionIntoView(form);
  }
}

export function renderMessage(app, message) {
  app.innerHTML = "";
  app.appendChild(createElement("p", "bhpf__message", message));
}

export function renderProductTestPage(app, quiz) {
  app.innerHTML = "";

  const header = createElement("div", "bhpf__header");
  header.appendChild(createElement("h2", "bhpf__title", quiz.title));
  header.appendChild(createElement("p", "bhpf__intro", "Product card preview"));

  const stage = createElement("div", "bhpf__stage bhpf__stage--results");
  const resultWrap = createElement("div", "bhpf__results");
  resultWrap.appendChild(createElement("h3", "bhpf__resultsTitle", "All product cards"));

  quiz.results.forEach((result) => {
    resultWrap.appendChild(renderResultCard(result));
  });

  stage.appendChild(resultWrap);
  app.appendChild(header);
  app.appendChild(stage);
}

function scrollActiveQuestionIntoView(form) {
  window.requestAnimationFrame(() => {
    const progressText = form.querySelector(".bhpf__progressText");
    if (progressText) {
      progressText.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  });
}

function renderResultsPage(app, quiz, answers) {
  app.innerHTML = "";

  const header = createElement("div", "bhpf__header");
  header.appendChild(createElement("h2", "bhpf__title", quiz.title));

  const stage = createElement("div", "bhpf__stage bhpf__stage--results");
  const resultWrap = createElement("div", "bhpf__results");

  renderResults(resultWrap, quiz, answers);
  stage.appendChild(resultWrap);
  app.appendChild(header);
  app.appendChild(stage);
  stage.scrollIntoView({ behavior: "smooth", block: "start" });
}

function renderProgress(currentIndex, questionCount) {
  const progress = createElement("div", "bhpf__progress");
  const text = createElement("p", "bhpf__progressText", "Question " + (currentIndex + 1) + " of " + questionCount);
  const track = createElement("div", "bhpf__progressTrack");
  const fill = createElement("span", "bhpf__progressFill");

  fill.style.width = ((currentIndex + 1) / questionCount * 100) + "%";
  track.appendChild(fill);
  progress.appendChild(text);
  progress.appendChild(track);

  return progress;
}

function renderQuestion(question, index, answers) {
  const fieldset = createElement("fieldset", "bhpf__question");
  fieldset.dataset.questionId = question.id;

  const legend = createElement("legend", "bhpf__questionTitle");
  legend.appendChild(createElement("span", "bhpf__questionNumber", String(index + 1)));
  legend.appendChild(document.createTextNode(question.question || "Question"));
  fieldset.appendChild(legend);

  if (question.subquestion) {
    fieldset.appendChild(createElement("p", "bhpf__subquestion", question.subquestion));
  }

  if (question.info) {
    fieldset.appendChild(renderInfo(question.info));
  }

  const options = createElement("div", "bhpf__options");
  const type = question.type === "multi" ? "checkbox" : "radio";

  (question.options || []).forEach((option) => {
    options.appendChild(renderOption(question, option, type, answers));
  });

  fieldset.appendChild(options);
  fieldset.appendChild(createElement("p", "bhpf__noResults", ""));
  fieldset.appendChild(createElement("p", "bhpf__error", "Please answer this question."));

  return fieldset;
}

function renderOption(question, option, type, answers) {
  const optionWrap = createElement("div", "bhpf__option");
  const label = createElement("label", "bhpf__optionLabel");
  const input = document.createElement("input");
  input.type = type;
  input.name = "bhpf_" + question.id;
  input.value = option.id;

  if (type === "radio") {
    input.checked = answers[question.id] === option.id;
  } else if (Array.isArray(answers[question.id])) {
    input.checked = answers[question.id].indexOf(option.id) !== -1;
  }

  optionWrap.classList.toggle("bhpf__option--selected", input.checked);

  input.addEventListener("change", () => {
    updateAnswer(question, option, input, type, answers);
    updateSelectedOptions(optionWrap.parentNode);
    clearNoResultsMessage(optionWrap.closest(".bhpf__question"));
  });

  optionWrap.addEventListener("click", (event) => {
    if (event.target === input || event.target.closest("details")) {
      return;
    }

    event.preventDefault();

    if (type === "radio") {
      input.checked = true;
    } else {
      input.checked = !input.checked;
    }

    updateAnswer(question, option, input, type, answers);
    updateSelectedOptions(optionWrap.parentNode);
    clearNoResultsMessage(optionWrap.closest(".bhpf__question"));
  });

  label.appendChild(input);
  label.appendChild(createElement("span", "bhpf__optionText", option.label || option.id));
  optionWrap.appendChild(label);

  if (option.description) {
    optionWrap.appendChild(createElement("p", "bhpf__optionDescription", option.description));
  }

  if (option.info) {
    optionWrap.appendChild(renderInfo(option.info));
  }

  return optionWrap;
}

function updateAnswer(question, option, input, type, answers) {
  if (type === "radio") {
    answers[question.id] = option.id;
    return;
  }

  if (!Array.isArray(answers[question.id])) {
    answers[question.id] = [];
  }

  if (input.checked) {
    if (answers[question.id].indexOf(option.id) === -1) {
      answers[question.id].push(option.id);
    }
    return;
  }

  answers[question.id] = answers[question.id].filter((selectedId) => selectedId !== option.id);
}

function updateSelectedOptions(options) {
  if (!options) {
    return;
  }

  options.querySelectorAll(".bhpf__option").forEach((option) => {
    const input = option.querySelector("input");
    option.classList.toggle("bhpf__option--selected", !!input && input.checked);
  });
}

function renderInfo(info) {
  const details = createElement("details", "bhpf__info");
  const summary = createElement("summary", "bhpf__infoSummary", info.label || "More info");
  const text = createElement("p", "bhpf__infoText", info.text || "");

  details.appendChild(summary);
  details.appendChild(text);

  return details;
}

function showMissingErrors(form, missing) {
  missing.forEach((question) => {
    const fieldset = form.querySelector('[data-question-id="' + cssEscape(question.id) + '"]');
    if (fieldset) {
      fieldset.classList.add("bhpf__question--error");
    }
  });

  const firstError = form.querySelector(".bhpf__question--error");
  if (firstError) {
    firstError.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

function clearErrors(form) {
  form.querySelectorAll(".bhpf__question--error").forEach((question) => {
    question.classList.remove("bhpf__question--error");
  });
}

function showNoResultsMessage(form, quiz) {
  const message = form.querySelector(".bhpf__noResults");
  if (!message) {
    return;
  }

  message.textContent = quiz.no_results_message || "No product matches all your requirements. Try changing this answer.";
  message.hidden = false;
  message.scrollIntoView({ behavior: "smooth", block: "center" });
}

function clearNoResultsMessage(question) {
  if (!question) {
    return;
  }

  const message = question.querySelector(".bhpf__noResults");
  if (!message) {
    return;
  }

  message.textContent = "";
  message.hidden = true;
}

function renderResults(resultWrap, quiz, answers) {
  const scoredResults = scoreResults(quiz, answers);
  const winner = scoredResults[0];
  const alternatives = scoredResults.slice(1, 3);

  resultWrap.innerHTML = "";
  resultWrap.appendChild(createElement("h3", "bhpf__resultsTitle", quiz.results_title || "Your recommendation"));

  if (!winner) {
    resultWrap.appendChild(createElement("p", "bhpf__intro", quiz.no_results_message || "No matching result was found."));
    return;
  }

  resultWrap.appendChild(renderResultCard(winner));

  if (alternatives.length) {
    resultWrap.appendChild(createElement("h4", "bhpf__alternativesTitle", "Alternatives for you"));
    alternatives.forEach((result) => {
      resultWrap.appendChild(renderResultCard(result, true));
    });
  }
}

function renderResultCard(result, isAlternative) {
  const card = createElement("article", isAlternative ? "bhpf__result bhpf__result--alternative" : "bhpf__result");
  const imageUrl = safeUrl(result.image);
  let affiliateButton = null;

  if (result.affiliate_html) {
    const affiliate = createElement("div", "bhpf__affiliate");
    affiliate.innerHTML = result.affiliate_html;
    affiliateButton = affiliate.querySelector(".amazon-card-link__button, .large-product-link__btn");
    if (affiliateButton) {
      affiliateButton.remove();
    }
    card.appendChild(affiliate);
  } else if (imageUrl) {
    const image = document.createElement("img");
    image.className = "bhpf__resultImage";
    image.src = imageUrl;
    image.alt = result.title || "Product recommendation";
    image.loading = "lazy";
    card.appendChild(image);
  }

  const content = createElement("div", "bhpf__resultContent");
  if (result.badge) {
    content.appendChild(createElement("p", "bhpf__resultBadge", result.badge));
  }

  content.appendChild(createElement("h4", "bhpf__resultTitle", result.title || "Recommended product"));

  if (result.summary) {
    content.appendChild(createElement("p", "bhpf__resultSummary", result.summary));
  }

  if (Array.isArray(result.features) && result.features.length) {
    const list = createElement("ul", "bhpf__features");
    result.features.forEach((feature) => {
      list.appendChild(createElement("li", "bhpf__feature", feature));
    });
    content.appendChild(list);
  }

  if (affiliateButton) {
    affiliateButton.classList.add("bhpf__resultLink");
    content.appendChild(affiliateButton);
  }

  const resultUrl = result.affiliate_html ? "" : safeUrl(result.url);
  if (resultUrl) {
    const link = createElement("a", "bhpf__resultLink", result.cta || "View product");
    link.href = resultUrl;
    link.rel = "nofollow sponsored noopener";
    content.appendChild(link);
  }

  card.appendChild(content);
  return card;
}
